// src/components/CallEvalDisplay.js
import React from 'react';

// Helper 1: Renders a single score item compactly
const ScoreItem = ({ name, value }) => (
    <span className="mr-3 inline-block"> {/* Add margin for spacing */}
        <span className="capitalize text-gray-600">{name}:</span>{' '}
        <span className="font-medium text-gray-800">{value ?? 'N/A'}</span>
    </span>
);

// Helper 2: Renders a category block with scores in a specific order
const ScoreCategoryBlock = ({ title, scores, prefixes, order }) => { // Accept 'order' prop

    // --- CORRECTED LOGIC: Process scores ONCE per category ---
    const categoryScores = Object.entries(scores) // Iterate over all scores ONCE
        .filter(([key, value]) => // Filter scores relevant to THIS category block
            // Check if the key starts with ANY of the prefixes for this group AND ends with _score
            prefixes.some(p => key.startsWith(p + '_')) &&
            key.endsWith('_score') &&
            (typeof value === 'number' || value === null || typeof value === 'string')
        )
        .map(([key, value]) => { // Map the filtered scores
             // --- Name and BaseKey Extraction (Same as before) ---
             let baseKey = key;
             let formattedName = key;

             if (baseKey.endsWith('_score')) {
                 baseKey = baseKey.substring(0, baseKey.length - '_score'.length);
                 formattedName = baseKey;
             }

             if (baseKey.startsWith('scarf_')) {
                 baseKey = baseKey.substring('scarf_'.length);
             } else if (baseKey.startsWith('call_structure_')) {
                 baseKey = baseKey.substring('call_structure_'.length);
             }

             // Formatting for display name
            if (formattedName.startsWith('call_structure_opening')) {
                formattedName = 'Opening';
            } else if (formattedName.startsWith('call_structure_info_gathering')) {
                formattedName = 'Info Gathering';
            } else if (formattedName.startsWith('call_structure_solution_presentation')) {
                formattedName = 'Solution Presentation';
            } else if (formattedName.startsWith('call_structure_closing')) {
                formattedName = 'Closing';
            } else if (formattedName.startsWith('scarf_')) {
                 formattedName = formattedName.substring('scarf_'.length);
            }
             formattedName = formattedName.replace(/_/g, ' ').trim();
             if (formattedName) {
                 formattedName = formattedName.charAt(0).toUpperCase() + formattedName.slice(1);
             }
             // --- End Name/BaseKey Logic ---

            return {
                name: formattedName || key,
                value: value === null ? 'N/A' : value,
                originalKey: key,
                baseKey: baseKey
            };
        })
        // --- Sorting Logic (Same as before) ---
        .sort((a, b) => {
            if (!order) {
                return a.name.localeCompare(b.name);
            }
            const indexA = order.indexOf(a.baseKey);
            const indexB = order.indexOf(b.baseKey);

            if (indexA === -1 && indexB === -1) {
                return a.name.localeCompare(b.name);
            }
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });
        // --- End Sorting Logic ---


    if (categoryScores.length === 0) return null; // Don't render block if no scores match

    // --- Rendering Logic (Same as before) ---
    return (
        <div className="mt-1">
            <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-0.5">{title}</h5>
            <div className="flex flex-wrap gap-x-2 gap-y-0">
                {categoryScores.map(score => (
                    <ScoreItem key={score.originalKey} name={score.name} value={score.value} />
                ))}
            </div>
        </div>
    );
};


// Main Component: Displays Call Evaluations
const CallEvalDisplay = ({ evaluations }) => {
    if (!Array.isArray(evaluations) || evaluations.length === 0) {
        return null;
    }

    // Define categories, prefixes, and the desired display ORDER
    // (This part remains the same)
    const categoryGroups = [
         {
            title: 'SCARF',
            prefixes: ['scarf'],
            order: ['status', 'certainty', 'autonomy', 'relatedness', 'fairness']
        },
        {
            title: 'Communication Effectiveness',
            prefixes: ['empathy', 'assertiveness', 'respect', 'clarity'],
            order: ['clarity', 'empathy', 'assertiveness', 'respect']
        },
        {
            title: 'Call Structure',
            prefixes: ['call_structure_opening', 'call_structure_info_gathering', 'call_structure_solution_presentation', 'call_structure_closing'],
            order: ['opening', 'info_gathering', 'solution_presentation', 'closing']
        },
    ];

    // --- Rendering Logic (Same as before) ---
    return (
        <div className="text-xs space-y-3 mt-1">
            {evaluations.map((ev, index) => (
                ev && ev.scores ? (
                    <div key={ev.staffId || index} className="border-t pt-2 first:border-t-0">
                        <p className="text-sm font-semibold text-blue-800 mb-1">{ev.staffName || 'Unknown Staff'}</p>
                        <div className="space-y-1">
                           {categoryGroups.map(group => (
                               <ScoreCategoryBlock
                                   key={group.title}
                                   title={group.title}
                                   scores={ev.scores}
                                   prefixes={group.prefixes}
                                   order={group.order}
                               />
                           ))}
                        </div>
                         <div className="mt-2">
                             <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Overall Score</h5>
                             <p className="text-sm font-bold">{ev.scores.overall_score ?? 'N/A'}</p>
                         </div>
                          <div className='mt-2 space-y-1'>
                             {ev.scores.key_strengths && <p className="text-xs"><strong className='text-gray-600'>Strengths:</strong> {ev.scores.key_strengths}</p>}
                             {ev.scores.areas_for_improvement && <p className="text-xs"><strong className='text-gray-600'>Improvements:</strong> {ev.scores.areas_for_improvement}</p>}
                          </div>
                    </div>
                 ) : null
            ))}
        </div>
    );
};

export default CallEvalDisplay;